import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  ShieldQuestion,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { mapTemplateStatusToPill } from "@/lib/assessments/statusPill";
import {
  ApiError,
  ASSESSMENT_GENERATION_MODE_DESCRIPTION,
  ASSESSMENT_GENERATION_MODE_LABEL,
  ASSESSMENT_PROCTOR_PROFILE_LABEL,
  QUESTION_DIFFICULTY_LABEL,
  QUESTION_DIFFICULTY_ORDER,
  QUESTION_LANGUAGE_LABEL,
  QUESTION_TYPE_LABEL,
  QUESTION_TYPE_ORDER,
  type AssessmentBlueprintItem,
  type AssessmentGenerationMode,
  type AssessmentProctorProfile,
  type AssessmentTemplatePublic,
  type AssessmentTemplateUpdate,
  type QuestionDifficulty,
  type QuestionLanguage,
  type QuestionType,
} from "@eduviaos/shared";
import {
  useActivateAssessmentTemplate,
  useArchiveAssessmentTemplate,
  useAssessmentTemplate,
  useAssessmentTemplateAvailability,
  useAssessmentTopics,
  useUpdateAssessmentTemplate,
} from "@/hooks/useAssessments";
import { useAuth } from "@/auth/AuthProvider";
import { useCanAuthorAssessments } from "@/hooks/useAssessmentAuthoring";
import { TenantAuthoringPausedBanner } from "@/components/assessments/TenantAuthoringPausedBanner";

/**
 * Phase H5 FE-1 — IA Assessments → Template detail + blueprint editor.
 *
 * Three sections, top to bottom:
 *   1. Identity + lifecycle — name, description, status pill, audit-y
 *      labels (activated_at + activated_by). Header carries Activate /
 *      Archive CTAs.
 *   2. Runtime config — grade band, language, generation mode, pool
 *      sizes, timer, max attempts, shuffle / pause / calculator
 *      toggles, proctor profile, post-attempt visibility.
 *   3. Blueprint editor — per-item rows with topic_slugs picker, count,
 *      type, difficulty, language override, marks_per_question,
 *      negative_marks override, randomise / human-only toggles, plus
 *      add / remove / reorder controls. Availability row badges show
 *      per-item bank deficit so activation surprises don't lurk.
 *
 * Edits stage locally; "Save changes" PATCHes once. Editing an active
 * template silently flips it back to draft (BE behaviour) — the page
 * surfaces this loudly with a banner after save so the SA isn't
 * surprised that they have to re-activate.
 */

type DraftBlueprintItem = AssessmentBlueprintItem & { _id: string };

interface DraftState {
  name: string;
  description: string;
  gradeMin: number;
  gradeMax: number;
  language: QuestionLanguage;
  items: DraftBlueprintItem[];
  generation_mode: AssessmentGenerationMode;
  pre_gen_pool_size: number;
  ai_top_up_threshold_pct: number;
  total_seconds: number;
  max_attempts: number;
  allow_pause: boolean;
  shuffle_questions: boolean;
  shuffle_options_within_question: boolean;
  calculator_allowed: boolean;
  language_picker_enabled: boolean;
  proctor_profile: AssessmentProctorProfile;
  show_correct_answers_in_report: boolean;
  apply_portal_show_results_after_outcome: boolean;
}

function templateToDraft(t: AssessmentTemplatePublic): DraftState {
  return {
    name: t.name,
    description: t.description ?? "",
    gradeMin: t.grade_band.min,
    gradeMax: t.grade_band.max,
    language: t.language,
    items: t.items.map((item, idx) => ({
      ...item,
      _id: `${idx}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    })),
    generation_mode: t.generation_mode,
    pre_gen_pool_size: t.pre_gen_pool_size,
    ai_top_up_threshold_pct: t.ai_top_up_threshold_pct,
    total_seconds: t.total_seconds,
    max_attempts: t.max_attempts,
    allow_pause: t.allow_pause,
    shuffle_questions: t.shuffle_questions,
    shuffle_options_within_question: t.shuffle_options_within_question,
    calculator_allowed: t.calculator_allowed,
    language_picker_enabled: t.language_picker_enabled,
    proctor_profile: t.proctor_profile,
    show_correct_answers_in_report: t.show_correct_answers_in_report,
    apply_portal_show_results_after_outcome: t.apply_portal_show_results_after_outcome,
  };
}

function nextRowId(prefix = "row") {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function AssessmentTemplateDetailPage() {
  const params = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const slug = params.slug ?? null;

  const templateQuery = useAssessmentTemplate(slug);
  const availabilityQuery = useAssessmentTemplateAvailability(slug);
  const topicsQuery = useAssessmentTopics();
  const updateMutation = useUpdateAssessmentTemplate();
  const activateMutation = useActivateAssessmentTemplate();
  const archiveMutation = useArchiveAssessmentTemplate();

  const [draft, setDraft] = React.useState<DraftState | null>(null);
  const [savedReopenedToDraft, setSavedReopenedToDraft] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [pendingActivate, setPendingActivate] = React.useState(false);
  const [activateNote, setActivateNote] = React.useState("");
  const [activateError, setActivateError] = React.useState<string | null>(null);
  const [pendingArchive, setPendingArchive] = React.useState(false);
  const [archiveReason, setArchiveReason] = React.useState("");
  const [archiveError, setArchiveError] = React.useState<string | null>(null);

  const { user } = useAuth();
  // Phase H5d — `canWrite` collapses RBAC + tenant-flag; `pausedByTenant`
  // distinguishes the "SA disabled authoring" surface so the banner can
  // explain the paused state instead of the generic permission-missing
  // copy.
  const { canAuthor: canWrite, pausedByTenant } = useCanAuthorAssessments();

  React.useEffect(() => {
    if (templateQuery.data) {
      setDraft(templateToDraft(templateQuery.data));
    }
  }, [templateQuery.data]);

  const template = templateQuery.data;
  const topics = topicsQuery.data?.items ?? [];
  const availability = availabilityQuery.data;

  const isDirty = React.useMemo(() => {
    if (!template || !draft) return false;
    const original = templateToDraft(template);
    return (
      JSON.stringify({
        ...draft,
        items: draft.items.map(({ _id, ...rest }) => rest),
      }) !==
      JSON.stringify({
        ...original,
        items: original.items.map(({ _id, ...rest }) => rest),
      })
    );
  }, [template, draft]);

  const blueprintDraftMarks = React.useMemo(() => {
    if (!draft) return 0;
    return draft.items.reduce(
      (sum, item) => sum + (item.marks_per_question ?? 1.0) * item.count,
      0,
    );
  }, [draft]);

  if (!slug) {
    return (
      <div className="mx-auto max-w-2xl py-20 text-center">
        <p className="text-muted-foreground text-sm">No template slug in the URL.</p>
      </div>
    );
  }

  // Iron Rule #14 fix: check error BEFORE the loader. Previously a 404 left
  // the page stuck on "Loading template…" forever because `!template` is
  // truthy when the request rejects. We also distinguish a 404 from a
  // transient error and render a dedicated not-found UI with a back link,
  // matching the rest of the IA shell's empty/error patterns.
  if (templateQuery.isError) {
    const err = templateQuery.error;
    const is404 = err instanceof ApiError && err.status === 404;
    if (is404) {
      return (
        <div className="mx-auto max-w-2xl py-20 text-center">
          <AlertTriangle aria-hidden="true" className="text-muted-foreground mx-auto size-8" />
          <h1 className="mt-4 text-lg font-semibold">Template not found</h1>
          <p className="text-muted-foreground mt-2 text-sm">
            No assessment template matches the slug{" "}
            <code className="bg-muted rounded px-1.5 py-0.5 text-xs">{slug}</code> in your
            institution. It may have been archived or never created.
          </p>
          <Button asChild variant="outline" size="sm" className="mt-6">
            <Link to="/assessments/templates">
              <ArrowLeft aria-hidden="true" className="size-3.5" />
              Back to templates
            </Link>
          </Button>
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-2xl py-12 text-center">
        <AlertCircle aria-hidden="true" className="text-destructive mx-auto size-6" />
        <p className="text-destructive mt-2 text-sm">
          Couldn&apos;t load template
          {err instanceof Error && err.message ? `: ${err.message}` : "."}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void templateQuery.refetch()}
          className="mt-4"
        >
          <RefreshCw aria-hidden="true" className="size-3.5" />
          Retry
        </Button>
      </div>
    );
  }

  if (templateQuery.isLoading || !draft || !template) {
    return (
      <div className="mx-auto max-w-2xl py-20 text-center">
        <Loader2 aria-hidden="true" className="text-muted-foreground mx-auto size-5 animate-spin" />
        <p className="text-muted-foreground mt-2 text-xs">Loading template…</p>
      </div>
    );
  }

  const status = mapTemplateStatusToPill(template.status);
  const updateItem = (id: string, patch: Partial<AssessmentBlueprintItem>) => {
    setDraft((d) =>
      d
        ? {
            ...d,
            items: d.items.map((item) => (item._id === id ? { ...item, ...patch } : item)),
          }
        : d,
    );
  };
  const removeItem = (id: string) => {
    setDraft((d) => (d ? { ...d, items: d.items.filter((item) => item._id !== id) } : d));
  };
  const reorderItem = (id: string, direction: "up" | "down") => {
    setDraft((d) => {
      if (!d) return d;
      const idx = d.items.findIndex((item) => item._id === id);
      const target = direction === "up" ? idx - 1 : idx + 1;
      if (idx < 0 || target < 0 || target >= d.items.length) return d;
      const items = [...d.items];
      const [moving] = items.splice(idx, 1);
      items.splice(target, 0, moving!);
      return { ...d, items };
    });
  };
  const addItem = () => {
    setDraft((d) =>
      d
        ? {
            ...d,
            items: [
              ...d.items,
              {
                _id: nextRowId(),
                topic_slugs: topics[0] ? [topics[0].slug] : [],
                count: 5,
                type: "mcq_single",
                difficulty: null,
                language: null,
                marks_per_question: 1.0,
                negative_marks: null,
                randomise_options: true,
                require_human_authored: false,
              },
            ],
          }
        : d,
    );
  };

  const validateAndBuildBody = (): AssessmentTemplateUpdate | string => {
    if (!draft.name.trim()) return "Name is required.";
    if (draft.gradeMin < 1 || draft.gradeMin > 12) return "Grade-min must be 1–12.";
    if (draft.gradeMax < 1 || draft.gradeMax > 12) return "Grade-max must be 1–12.";
    if (draft.gradeMin > draft.gradeMax) return "Grade-min must be ≤ grade-max.";
    if (draft.items.length === 0) return "Add at least one blueprint item before saving.";
    if (draft.items.length > 50) return "A template can carry at most 50 blueprint items.";
    for (const [i, item] of draft.items.entries()) {
      if (item.topic_slugs.length === 0) return `Row ${i + 1}: pick at least one topic.`;
      if (item.count < 1 || item.count > 200) return `Row ${i + 1}: count must be 1–200.`;
      if ((item.marks_per_question ?? 1) < 0 || (item.marks_per_question ?? 1) > 100)
        return `Row ${i + 1}: marks per question must be 0–100.`;
    }
    if (draft.total_seconds < 60 || draft.total_seconds > 8 * 3600)
      return "Total time must be 60–28 800 seconds (1 min to 8 hrs).";
    if (draft.max_attempts < 1 || draft.max_attempts > 10) return "Max attempts must be 1–10.";
    if (draft.pre_gen_pool_size < 0 || draft.pre_gen_pool_size > 200)
      return "Pool size must be 0–200.";
    if (draft.ai_top_up_threshold_pct < 0 || draft.ai_top_up_threshold_pct > 100)
      return "AI top-up threshold must be 0–100%.";

    const items: AssessmentBlueprintItem[] = draft.items.map(({ _id: _, ...rest }) => rest);
    return {
      name: draft.name.trim(),
      description: draft.description.trim() || null,
      grade_band: { min: draft.gradeMin, max: draft.gradeMax },
      language: draft.language,
      items,
      generation_mode: draft.generation_mode,
      pre_gen_pool_size: draft.pre_gen_pool_size,
      ai_top_up_threshold_pct: draft.ai_top_up_threshold_pct,
      total_seconds: draft.total_seconds,
      max_attempts: draft.max_attempts,
      allow_pause: draft.allow_pause,
      shuffle_questions: draft.shuffle_questions,
      shuffle_options_within_question: draft.shuffle_options_within_question,
      calculator_allowed: draft.calculator_allowed,
      language_picker_enabled: draft.language_picker_enabled,
      proctor_profile: draft.proctor_profile,
      show_correct_answers_in_report: draft.show_correct_answers_in_report,
      apply_portal_show_results_after_outcome: draft.apply_portal_show_results_after_outcome,
    };
  };

  const handleSave = async () => {
    setErrorMessage(null);
    setSavedReopenedToDraft(false);
    const result = validateAndBuildBody();
    if (typeof result === "string") {
      setErrorMessage(result);
      return;
    }
    try {
      const wasActive = template.status === "active";
      const saved = await updateMutation.mutateAsync({ slug: template.slug, body: result });
      setSavedReopenedToDraft(wasActive && saved.status === "draft");
      toast.success("Saved blueprint changes.");
    } catch (raw) {
      setErrorMessage(extractMessage(raw, "Could not save — please try again."));
    }
  };

  const handleResetDraft = () => {
    setDraft(templateToDraft(template));
    setErrorMessage(null);
    setSavedReopenedToDraft(false);
  };

  const handleConfirmActivate = async () => {
    setActivateError(null);
    try {
      await activateMutation.mutateAsync({
        slug: template.slug,
        note: activateNote.trim() || undefined,
      });
      toast.success(`Activated "${template.name}".`);
      setPendingActivate(false);
      setActivateNote("");
      setSavedReopenedToDraft(false);
    } catch (raw) {
      setActivateError(extractMessage(raw, "Could not activate — check blueprint coverage."));
    }
  };

  const handleConfirmArchive = async () => {
    setArchiveError(null);
    if (archiveReason.trim().length < 3) {
      setArchiveError("Reason must be at least 3 characters.");
      return;
    }
    try {
      await archiveMutation.mutateAsync({ slug: template.slug, reason: archiveReason.trim() });
      toast.success(`Archived "${template.name}".`);
      navigate("/assessments/templates");
    } catch (raw) {
      setArchiveError(extractMessage(raw, "Could not archive — please try again."));
    }
  };

  return (
    <div className="mx-auto max-w-7xl py-8" data-testid="template-detail">
      <Button variant="ghost" size="sm" asChild className="mb-4">
        <Link to="/assessments/templates">
          <ArrowLeft aria-hidden="true" className="size-4" />
          Back to Templates
        </Link>
      </Button>

      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>
              <span className="flex items-center gap-3">
                {template.name}
                <StatusPill status={status} />
              </span>
            </PageHeaderHeading>
            <PageHeaderDescription>
              {template.items.length} items · {template.total_marks} marks · grade-band{" "}
              {template.grade_band.min}–{template.grade_band.max}
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            {canWrite && template.status === "draft" ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setActivateError(null);
                  setActivateNote("");
                  setPendingActivate(true);
                }}
                disabled={isDirty}
              >
                <CheckCircle2 aria-hidden="true" className="size-4" />
                Activate
              </Button>
            ) : null}
            {canWrite && template.status !== "archived" ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setArchiveError(null);
                  setArchiveReason("");
                  setPendingArchive(true);
                }}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 aria-hidden="true" className="size-4" />
                Archive
              </Button>
            ) : null}
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      {savedReopenedToDraft ? (
        <div
          role="alert"
          className="mt-4 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
        >
          <AlertTriangle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            Your edit reopened this template back to draft (BE invariant). Use the Activate button
            once the blueprint is fulfillable again so it returns to active.
          </span>
        </div>
      ) : null}

      {pausedByTenant ? <TenantAuthoringPausedBanner className="mt-4" /> : null}

      {!canWrite && user && !pausedByTenant ? (
        <div
          role="status"
          className="border-border/60 bg-muted/40 text-muted-foreground mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <ShieldQuestion
            aria-hidden="true"
            className="text-muted-foreground mt-0.5 size-3.5 shrink-0"
          />
          <span className="flex-1">
            Read-only view. Editing templates needs the{" "}
            <code className="text-foreground">manage_question_bank</code> permission.
          </span>
        </div>
      ) : null}

      {errorMessage ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">{errorMessage}</span>
        </div>
      ) : null}

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Identity</CardTitle>
            <CardDescription>Name, description, grade band, default language.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="t-name">Name</Label>
              <Input
                id="t-name"
                value={draft.name}
                onChange={(e) => setDraft((d) => (d ? { ...d, name: e.target.value } : d))}
                maxLength={200}
                disabled={!canWrite}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="t-desc">Description (optional)</Label>
              <Textarea
                id="t-desc"
                value={draft.description}
                onChange={(e) => setDraft((d) => (d ? { ...d, description: e.target.value } : d))}
                maxLength={2000}
                rows={3}
                disabled={!canWrite}
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="grid gap-1">
                <Label htmlFor="t-grade-min" className="text-xs">
                  Grade min
                </Label>
                <Input
                  id="t-grade-min"
                  type="number"
                  min={1}
                  max={12}
                  value={draft.gradeMin}
                  onChange={(e) =>
                    setDraft((d) => (d ? { ...d, gradeMin: Number(e.target.value) || 1 } : d))
                  }
                  disabled={!canWrite}
                />
              </div>
              <div className="grid gap-1">
                <Label htmlFor="t-grade-max" className="text-xs">
                  Grade max
                </Label>
                <Input
                  id="t-grade-max"
                  type="number"
                  min={1}
                  max={12}
                  value={draft.gradeMax}
                  onChange={(e) =>
                    setDraft((d) => (d ? { ...d, gradeMax: Number(e.target.value) || 1 } : d))
                  }
                  disabled={!canWrite}
                />
              </div>
              <div className="grid gap-1">
                <Label className="text-xs">Default language</Label>
                <Select
                  value={draft.language}
                  onValueChange={(v) =>
                    setDraft((d) => (d ? { ...d, language: v as QuestionLanguage } : d))
                  }
                  disabled={!canWrite}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">{QUESTION_LANGUAGE_LABEL.en}</SelectItem>
                    <SelectItem value="hi">{QUESTION_LANGUAGE_LABEL.hi}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Audit</CardTitle>
            <CardDescription>What the SA + BE pinned at create / last save.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2 text-xs">
            <Field label="Status">
              <StatusPill status={status} />
            </Field>
            <Field label="Template version">
              <span className="tabular-nums">
                {template.activated_at ? "Bumped on activate" : "0 (draft)"}
              </span>
            </Field>
            <Field label="Activated at">
              <span className="font-mono">{template.activated_at ?? "—"}</span>
            </Field>
            <Field label="Activated by">
              <span className="font-mono">{template.activated_by_user_id ?? "—"}</span>
            </Field>
            <Field label="Created">
              <span className="font-mono">{template.created_at}</span>
            </Field>
            <Field label="Last edited">
              <span className="font-mono">{template.updated_at}</span>
            </Field>
            <Field label="Computed total marks">
              <span className="tabular-nums">{blueprintDraftMarks}</span>
            </Field>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Runtime + delivery</CardTitle>
          <CardDescription>How the system materialises and serves attempts.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label>Generation mode</Label>
              <Select
                value={draft.generation_mode}
                onValueChange={(v) =>
                  setDraft((d) =>
                    d ? { ...d, generation_mode: v as AssessmentGenerationMode } : d,
                  )
                }
                disabled={!canWrite}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hybrid">{ASSESSMENT_GENERATION_MODE_LABEL.hybrid}</SelectItem>
                  <SelectItem value="pre_gen">
                    {ASSESSMENT_GENERATION_MODE_LABEL.pre_gen}
                  </SelectItem>
                  <SelectItem value="on_demand">
                    {ASSESSMENT_GENERATION_MODE_LABEL.on_demand}
                  </SelectItem>
                </SelectContent>
              </Select>
              <span className="text-muted-foreground text-[11px] leading-snug">
                {ASSESSMENT_GENERATION_MODE_DESCRIPTION[draft.generation_mode]}
              </span>
            </div>
            <div className="grid gap-1.5">
              <Label>Proctor profile</Label>
              <Select
                value={draft.proctor_profile}
                onValueChange={(v) =>
                  setDraft((d) =>
                    d ? { ...d, proctor_profile: v as AssessmentProctorProfile } : d,
                  )
                }
                disabled={!canWrite}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{ASSESSMENT_PROCTOR_PROFILE_LABEL.none}</SelectItem>
                  <SelectItem value="basic">{ASSESSMENT_PROCTOR_PROFILE_LABEL.basic}</SelectItem>
                  <SelectItem value="strict">{ASSESSMENT_PROCTOR_PROFILE_LABEL.strict}</SelectItem>
                  <SelectItem value="strict_plus_id">
                    {ASSESSMENT_PROCTOR_PROFILE_LABEL.strict_plus_id}
                  </SelectItem>
                </SelectContent>
              </Select>
              <span className="text-muted-foreground text-[11px] leading-snug">
                Stricter profiles enforce fullscreen / webcam / ID checks; the runtime layer wires
                capability resolution.
              </span>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-4">
            <NumberField
              id="pool-size"
              label="Pool size"
              value={draft.pre_gen_pool_size}
              min={0}
              max={200}
              suffix="papers"
              disabled={!canWrite || draft.generation_mode === "on_demand"}
              onChange={(n) => setDraft((d) => (d ? { ...d, pre_gen_pool_size: n } : d))}
            />
            <NumberField
              id="ai-top-up"
              label="AI top-up at"
              value={draft.ai_top_up_threshold_pct}
              min={0}
              max={100}
              suffix="%"
              disabled={!canWrite || draft.generation_mode === "on_demand"}
              onChange={(n) => setDraft((d) => (d ? { ...d, ai_top_up_threshold_pct: n } : d))}
            />
            <NumberField
              id="total-seconds"
              label="Total time"
              value={Math.round(draft.total_seconds / 60)}
              min={1}
              max={8 * 60}
              suffix="min"
              disabled={!canWrite}
              onChange={(n) => setDraft((d) => (d ? { ...d, total_seconds: n * 60 } : d))}
            />
            <NumberField
              id="max-attempts"
              label="Max attempts"
              value={draft.max_attempts}
              min={1}
              max={10}
              suffix="attempts"
              disabled={!canWrite}
              onChange={(n) => setDraft((d) => (d ? { ...d, max_attempts: n } : d))}
            />
          </div>

          <div className="grid gap-2 sm:grid-cols-3">
            <Toggle
              id="t-shuffle-q"
              checked={draft.shuffle_questions}
              disabled={!canWrite}
              onChange={(v) => setDraft((d) => (d ? { ...d, shuffle_questions: v } : d))}
              label="Shuffle questions"
              helper="Randomise question order per attempt."
            />
            <Toggle
              id="t-shuffle-opts"
              checked={draft.shuffle_options_within_question}
              disabled={!canWrite}
              onChange={(v) =>
                setDraft((d) => (d ? { ...d, shuffle_options_within_question: v } : d))
              }
              label="Shuffle options"
              helper="Randomise option order within each MCQ."
            />
            <Toggle
              id="t-pause"
              checked={draft.allow_pause}
              disabled={!canWrite}
              onChange={(v) => setDraft((d) => (d ? { ...d, allow_pause: v } : d))}
              label="Allow pause"
              helper="Applicant can pause the timer between questions."
            />
            <Toggle
              id="t-calc"
              checked={draft.calculator_allowed}
              disabled={!canWrite}
              onChange={(v) => setDraft((d) => (d ? { ...d, calculator_allowed: v } : d))}
              label="Calculator allowed"
              helper="Surfaces an on-screen calculator in the runtime."
            />
            <Toggle
              id="t-langpick"
              checked={draft.language_picker_enabled}
              disabled={!canWrite}
              onChange={(v) => setDraft((d) => (d ? { ...d, language_picker_enabled: v } : d))}
              label="Language picker"
              helper="Shows the en/hi toggle in the runtime header."
            />
            <Toggle
              id="t-show-correct"
              checked={draft.show_correct_answers_in_report}
              disabled={!canWrite}
              onChange={(v) =>
                setDraft((d) => (d ? { ...d, show_correct_answers_in_report: v } : d))
              }
              label="Show correct in report"
              helper="Reveal correct answers in the post-submit report."
            />
            <Toggle
              id="t-portal-show"
              checked={draft.apply_portal_show_results_after_outcome}
              disabled={!canWrite}
              onChange={(v) =>
                setDraft((d) => (d ? { ...d, apply_portal_show_results_after_outcome: v } : d))
              }
              label="Show result on apply portal"
              helper="Otherwise applicants are told their result was emailed."
            />
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader className="flex-row items-center justify-between gap-2 pb-3">
          <div>
            <CardTitle className="text-base">Blueprint</CardTitle>
            <CardDescription>
              Each row draws N questions matching the filter. The engine UNIONs candidates across
              topics.
            </CardDescription>
          </div>
          {canWrite ? (
            <Button size="sm" variant="outline" onClick={addItem}>
              <Plus aria-hidden="true" className="size-3.5" />
              Add item
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="grid gap-3">
          {topics.length === 0 ? (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
            >
              <Sparkles aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              <span className="flex-1">
                No topics yet —{" "}
                <Link to="/assessments/topics" className="underline">
                  author at least one
                </Link>{" "}
                before adding blueprint items.
              </span>
            </div>
          ) : null}
          {draft.items.length === 0 ? (
            <p className="text-muted-foreground border-border/60 rounded-md border border-dashed px-3 py-4 text-center text-xs">
              No blueprint items yet. Add one to get started.
            </p>
          ) : (
            draft.items.map((item, idx) => (
              <BlueprintItemRow
                key={item._id}
                item={item}
                idx={idx}
                total={draft.items.length}
                topics={topics}
                canWrite={canWrite}
                availability={availability?.rows.find((r) => r.index === idx)}
                onChange={(patch) => updateItem(item._id, patch)}
                onRemove={() => removeItem(item._id)}
                onMoveUp={() => reorderItem(item._id, "up")}
                onMoveDown={() => reorderItem(item._id, "down")}
              />
            ))
          )}

          {availability?.has_deficit ? (
            <div
              role="alert"
              className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
            >
              <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              <span className="flex-1">
                One or more blueprint items have a deficit — activation will fail until the bank
                catches up. Add or approve more questions for the deficient topic / type /
                difficulty combinations.
              </span>
            </div>
          ) : !availabilityQuery.isLoading && availability && availability.rows.length > 0 ? (
            <div className="flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300">
              <ShieldCheck aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              <span className="flex-1">
                Bank coverage is healthy — this blueprint can be activated.
              </span>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {canWrite ? (
        <div className="mt-4 flex items-center justify-end gap-2">
          {isDirty ? (
            <Button variant="ghost" onClick={handleResetDraft} disabled={updateMutation.isPending}>
              Discard
            </Button>
          ) : null}
          <Button onClick={() => void handleSave()} disabled={!isDirty || updateMutation.isPending}>
            {updateMutation.isPending ? (
              <>
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save aria-hidden="true" className="size-4" />
                Save changes
              </>
            )}
          </Button>
        </div>
      ) : null}

      <Dialog
        open={pendingActivate}
        onOpenChange={(open) => {
          if (!open) {
            setPendingActivate(false);
            setActivateError(null);
            setActivateNote("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Activate {template.name}?</DialogTitle>
            <DialogDescription>
              The BE pre-flights the blueprint against the question bank. If any item lacks approved
              questions, activation fails loudly with the deficit list.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="activate-note-detail">Note (optional)</Label>
            <Textarea
              id="activate-note-detail"
              value={activateNote}
              onChange={(e) => setActivateNote(e.target.value)}
              placeholder="Activated for the 2026 admissions cycle."
              maxLength={500}
              rows={2}
            />
          </div>
          {activateError ? (
            <div className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
              {activateError}
            </div>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={activateMutation.isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={() => void handleConfirmActivate()}
              disabled={activateMutation.isPending}
            >
              {activateMutation.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  Activating…
                </>
              ) : (
                <>
                  <CheckCircle2 aria-hidden="true" className="size-4" />
                  Activate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingArchive}
        onOpenChange={(open) => {
          if (!open) {
            setPendingArchive(false);
            setArchiveError(null);
            setArchiveReason("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive {template.name}?</DialogTitle>
            <DialogDescription>
              Archived templates can't be assigned to new attempts. Existing attempts remain valid
              because each attempt snapshots its question set.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="archive-detail-reason">Reason (3–500 chars)</Label>
            <Textarea
              id="archive-detail-reason"
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="Replaced by the 2026 blueprint."
              maxLength={500}
              rows={3}
            />
          </div>
          {archiveError ? (
            <div className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
              {archiveError}
            </div>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={archiveMutation.isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={() => void handleConfirmArchive()}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  Archiving…
                </>
              ) : (
                <>
                  <Trash2 aria-hidden="true" className="size-4" />
                  Archive template
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BlueprintItemRow({
  item,
  idx,
  total,
  topics,
  canWrite,
  availability,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  item: DraftBlueprintItem;
  idx: number;
  total: number;
  topics: { slug: string; name: string }[];
  canWrite: boolean;
  availability?: { available: number; requested: number; deficit: number };
  onChange: (patch: Partial<AssessmentBlueprintItem>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const hasDeficit = availability && availability.deficit > 0;
  return (
    <div
      className={cn(
        "border-border/60 grid gap-3 rounded-md border p-3",
        hasDeficit && "border-destructive/40 bg-destructive/5",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="bg-muted text-muted-foreground rounded-md px-2 py-0.5 text-xs font-medium tabular-nums">
            #{idx + 1}
          </span>
          {availability ? (
            <Badge
              variant="outline"
              className={cn(
                "border-border/60 text-[10px] uppercase",
                hasDeficit && "border-destructive text-destructive",
                !hasDeficit && "border-emerald-500/40 text-emerald-700 dark:text-emerald-300",
              )}
            >
              {hasDeficit
                ? `Deficit ${availability.deficit}`
                : `Bank ${availability.available} ≥ ${availability.requested}`}
            </Badge>
          ) : null}
        </div>
        {canWrite ? (
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7"
              disabled={idx === 0}
              onClick={onMoveUp}
              aria-label="Move up"
            >
              <ChevronUp aria-hidden="true" className="size-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7"
              disabled={idx === total - 1}
              onClick={onMoveDown}
              aria-label="Move down"
            >
              <ChevronDown aria-hidden="true" className="size-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-destructive size-7"
              onClick={onRemove}
              aria-label="Remove item"
            >
              <X aria-hidden="true" className="size-3.5" />
            </Button>
          </div>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Label className="text-xs">Topics</Label>
          <TopicMultiSelect
            value={item.topic_slugs}
            options={topics}
            disabled={!canWrite}
            onChange={(next) => onChange({ topic_slugs: next })}
          />
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Type</Label>
          <Select
            value={item.type}
            onValueChange={(v) => onChange({ type: v as QuestionType })}
            disabled={!canWrite}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {QUESTION_TYPE_ORDER.map((t) => (
                <SelectItem key={t} value={t}>
                  {QUESTION_TYPE_LABEL[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <NumberField
          id={`item-${item._id}-count`}
          label="Count"
          value={item.count}
          min={1}
          max={200}
          disabled={!canWrite}
          onChange={(n) => onChange({ count: n })}
        />
        <div className="grid gap-1.5">
          <Label className="text-xs">Difficulty</Label>
          <Select
            value={item.difficulty ?? "__any__"}
            onValueChange={(v) =>
              onChange({ difficulty: v === "__any__" ? null : (v as QuestionDifficulty) })
            }
            disabled={!canWrite}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Any</SelectItem>
              {QUESTION_DIFFICULTY_ORDER.map((d) => (
                <SelectItem key={d} value={d}>
                  {QUESTION_DIFFICULTY_LABEL[d]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Language</Label>
          <Select
            value={item.language ?? "__any__"}
            onValueChange={(v) =>
              onChange({ language: v === "__any__" ? null : (v as QuestionLanguage) })
            }
            disabled={!canWrite}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Template default</SelectItem>
              <SelectItem value="en">{QUESTION_LANGUAGE_LABEL.en}</SelectItem>
              <SelectItem value="hi">{QUESTION_LANGUAGE_LABEL.hi}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <NumberField
          id={`item-${item._id}-marks`}
          label="Marks / Q"
          value={item.marks_per_question ?? 1}
          min={0}
          max={100}
          step={0.5}
          disabled={!canWrite}
          onChange={(n) => onChange({ marks_per_question: n })}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <NullableNumberField
          id={`item-${item._id}-neg`}
          label="Negative marks override"
          placeholder="—"
          value={item.negative_marks ?? null}
          min={0}
          max={100}
          step={0.25}
          disabled={!canWrite}
          onChange={(n) => onChange({ negative_marks: n })}
        />
        <Toggle
          id={`item-${item._id}-rand`}
          checked={item.randomise_options ?? true}
          disabled={!canWrite}
          onChange={(v) => onChange({ randomise_options: v })}
          label="Randomise options"
          helper="Per-item override of the template-level shuffle."
        />
        <Toggle
          id={`item-${item._id}-human`}
          checked={item.require_human_authored ?? false}
          disabled={!canWrite}
          onChange={(v) => onChange({ require_human_authored: v })}
          label="Require human authored"
          helper="Refuses AI-generated candidates for this row."
        />
      </div>
    </div>
  );
}

function TopicMultiSelect({
  value,
  options,
  disabled,
  onChange,
}: {
  value: string[];
  options: { slug: string; name: string }[];
  disabled?: boolean;
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const selectedSet = new Set(value);
  const summary =
    value.length === 0
      ? "No topics"
      : value.length === 1
        ? (options.find((o) => o.slug === value[0])?.name ?? value[0])
        : `${value.length} topics selected`;
  return (
    <div className="relative">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className="w-full justify-between"
      >
        <span className={cn("truncate", value.length === 0 && "text-muted-foreground")}>
          {summary}
        </span>
        <ChevronDown aria-hidden="true" className="size-3.5" />
      </Button>
      {open && !disabled ? (
        <div className="bg-popover border-border absolute z-20 mt-1 grid max-h-60 w-full gap-0.5 overflow-y-auto rounded-md border p-1 shadow-md">
          {options.length === 0 ? (
            <span className="text-muted-foreground px-2 py-1.5 text-xs">No topics</span>
          ) : (
            options.map((opt) => {
              const checked = selectedSet.has(opt.slug);
              return (
                <button
                  key={opt.slug}
                  type="button"
                  className="hover:bg-muted/60 flex items-center gap-2 rounded-sm px-2 py-1 text-left text-xs"
                  onClick={() => {
                    onChange(checked ? value.filter((v) => v !== opt.slug) : [...value, opt.slug]);
                  }}
                >
                  <Checkbox checked={checked} aria-hidden="true" />
                  <span className="text-foreground">{opt.name}</span>
                </button>
              );
            })
          )}
          <div className="mt-1 flex justify-end border-t pt-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setOpen(false)}
            >
              Done
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-center gap-2">
      <span className="text-muted-foreground text-[11px] uppercase tracking-wide">{label}</span>
      <span className="text-foreground text-xs">{children}</span>
    </div>
  );
}

interface NumberFieldProps {
  id: string;
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  disabled?: boolean;
  onChange: (next: number) => void;
}

function NumberField({
  id,
  label,
  value,
  min,
  max,
  step,
  suffix,
  disabled,
  onChange,
}: NumberFieldProps) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              onChange(min ?? 0);
              return;
            }
            const parsed = Number(raw);
            if (Number.isNaN(parsed)) return;
            onChange(parsed);
          }}
        />
        {suffix ? <span className="text-muted-foreground text-[11px]">{suffix}</span> : null}
      </div>
    </div>
  );
}

interface NullableNumberFieldProps {
  id: string;
  label: string;
  value: number | null;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  placeholder?: string;
  disabled?: boolean;
  onChange: (next: number | null) => void;
}

function NullableNumberField({
  id,
  label,
  value,
  min,
  max,
  step,
  suffix,
  placeholder,
  disabled,
  onChange,
}: NullableNumberFieldProps) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="number"
          min={min}
          max={max}
          step={step}
          value={value ?? ""}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              onChange(null);
              return;
            }
            const parsed = Number(raw);
            if (Number.isNaN(parsed)) return;
            onChange(parsed);
          }}
        />
        {suffix ? <span className="text-muted-foreground text-[11px]">{suffix}</span> : null}
      </div>
    </div>
  );
}

interface ToggleProps {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  label: string;
  helper?: string;
}

function Toggle({ id, checked, disabled, onChange, label, helper }: ToggleProps) {
  return (
    <label
      htmlFor={id}
      className={cn(
        "border-border/60 bg-background flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(v) => onChange(v === true)}
      />
      <span className="grid gap-0.5">
        <span className="text-foreground text-xs font-medium">{label}</span>
        {helper ? <span className="text-muted-foreground text-[10px]">{helper}</span> : null}
      </span>
    </label>
  );
}

function extractMessage(raw: unknown, fallback: string): string {
  if (raw instanceof ApiError && raw.message) return raw.message;
  if (raw instanceof Error && raw.message) return raw.message;
  return fallback;
}
